/**
 * Smart Navigation Utility
 * 
 * Centralized logic to determine which screen to navigate to next
 * based on what data is already available in the application state.
 * 
 * Query Parameters:
 * - smartNavigationDisabled=true: Disables smart navigation and forces visiting all screens
 */

import { AppStateType } from '../state/AppState';
import { isSelfPay } from './insuranceUtils';

/**
 * Screen flow configuration with required fields and navigation order
 */
const SCREEN_REQUIREMENTS = [
  { id: 'f', fields: ['stateString'], name: 'Location Selection', order: 1 },
  { id: 'g', fields: ['service'], name: 'Service Selection', order: 2 },
  { id: 'h', fields: ['insurance'], name: 'Insurance Selection', order: 3 },
  { id: 'i', fields: ['locationCode'], name: 'Location Selection', order: 4 },
  { id: 'j', fields: ['reason'], name: 'Reason Selection', order: 5 },
  { id: 'k', fields: ['ageGroup'], name: 'Age Group Selection', order: 6 },
  { id: 'e', fields: [], name: 'Region Selection', order: 7 },
  { id: 'l', fields: ['existingClient'], name: 'Existing Client Selection', order: 8 },
  { id: 'm', fields: ['appointmentDate', 'appointmentTime'], name: 'Date/Time Selection', order: 9 },
  { id: 'p', fields: ['visitType'], name: 'Patient Form', order: 10 }
];

/**
 * Check if a field has a meaningful value
 */
function hasValue(value: any): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  if (typeof value === 'string' && isSelfPay(value)) return false; // Default insurance (self-pay)
  return true;
}

/**
 * Check if smart navigation is disabled via query parameter
 */
function isSmartNavigationDisabled(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('smartNavigationDisabled') === 'true';
}

/**
 * Check if all required fields for a screen are present
 */
function hasRequiredData(state: AppStateType, fields: string[]): boolean {
  // If smart navigation is disabled, always go to each screen
  if (isSmartNavigationDisabled()) {
    return false;
  }
  
  // If fields list is empty, always go to that screen
  if (fields.length === 0) {
    return false;
  }
  
  return fields.every(field => hasValue(state[field as keyof AppStateType]));
}

/**
 * Get the next screen that needs data (legacy function for backward compatibility)
 * 
 * @param state - Current application state
 * @returns The screen ID of the next incomplete screen, or 'e' as fallback
 */
export function getNextScreen(state: AppStateType): string {
  for (const screen of SCREEN_REQUIREMENTS) {
    const hasData = hasRequiredData(state, screen.fields);
    const isAlwaysVisit = screen.fields.length === 0;
    
    
    if (!hasData) {
      
      return screen.id;
    }
  }
  return 'e'; // Fallback
}

/**
 * Get the next screen from a starting point, navigating forward through the flow
 * 
 * @param state - Current application state
 * @param startingScreen - The screen to start from (e.g., 'j')
 * @returns The next screen that needs data, starting from the given screen
 */
export function getNextScreenFrom(state: AppStateType, startingScreen: string): string {
  // Find the starting screen's order
  const startingScreenConfig = SCREEN_REQUIREMENTS.find(screen => screen.id === startingScreen);
  if (!startingScreenConfig) {
    return getNextScreen(state);
  }
  
  const startingOrder = startingScreenConfig.order;
  
  
  // Get screens that come after the starting screen
  const subsequentScreens = SCREEN_REQUIREMENTS
    .filter(screen => screen.order > startingOrder)
    .sort((a, b) => a.order - b.order);
  // Find the first screen that needs data
  for (const screen of subsequentScreens) {
    const hasData = hasRequiredData(state, screen.fields);
    const isAlwaysVisit = screen.fields.length === 0;
    
    
    if (!hasData) {
      
      return screen.id;
    } else {
      // Log what data is causing the skip
      const skipReasons = screen.fields.map(field => {
        const value = state[field as keyof AppStateType];
        return `${field}: ${value}`;
      }).join(', ');
      
    }
  }
  return 'e'; // Fallback
}

/**
 * Get the next screen that needs data, but skip screens that already have complete data
 * This is useful when you want to continue from where you left off
 * 
 * @param state - Current application state
 * @returns The screen ID of the next incomplete screen
 */
export function getNextIncompleteScreen(state: AppStateType): string {
  for (const screen of SCREEN_REQUIREMENTS) {
    const hasData = hasRequiredData(state, screen.fields);
    const isAlwaysVisit = screen.fields.length === 0;
    
    
    if (!hasData) {
      
      return screen.id;
    } else {
      // Log what data is causing the skip
      const skipReasons = screen.fields.map(field => {
        const value = state[field as keyof AppStateType];
        return `${field}: ${value}`;
      }).join(', ');
      
    }
  }
  return 'e'; // Fallback
}

/**
 * Get the navigation path for a screen
 */
export function getScreenPath(screenId: string): string {
  return `/${screenId}`;
}

/**
 * Navigate to the next screen from a starting point
 * This is a convenience function that combines getNextScreenFrom and getScreenPath
 * 
 * @param state - Current application state
 * @param startingScreen - The screen to start from
 * @param navigate - The navigate function from react-router-dom
 */
export function navigateToNextScreenFrom(
  state: AppStateType, 
  startingScreen: string, 
  navigate: (path: string) => void
): void {
  const nextScreen = getNextScreenFrom(state, startingScreen);
  const path = getScreenPath(nextScreen);
  
  navigate(path);
}

/**
 * Navigate to the next incomplete screen
 * This is a convenience function that combines getNextIncompleteScreen and getScreenPath
 * 
 * @param state - Current application state
 * @param navigate - The navigate function from react-router-dom
 */
export function navigateToNextIncompleteScreen(
  state: AppStateType, 
  navigate: (path: string) => void
): void {
  const nextScreen = getNextIncompleteScreen(state);
  const path = getScreenPath(nextScreen);
  
  navigate(path);
}
